import {
  MODULE_STATUS,
  studentProgressCell,
} from '../../utils/moduleProgressDisplay';

/**
 * Students × modules matrix with Completed / Ongoing toggles.
 * Uses native <select> for dense 2-option cells (SearchableSelect is too heavy here).
 */
export default function ModuleCompletionMatrix({
  matrix,
  savingKey,
  onStatusChange,
}) {
  if (!matrix) return null;

  const { modules = [], students = [], summary } = matrix;

  if (!modules.length) {
    return (
      <p className="staff-page-lead">
        This course has no modules assigned yet. An admin should open{' '}
        <strong>Courses</strong>, edit this course, and add modules from the catalog (or create
        them under <strong>Modules</strong> first). After that, refresh this page to track
        Completed / Ongoing.
      </p>
    );
  }

  if (!students.length) {
    return <p className="staff-page-lead">No students enrolled in this batch.</p>;
  }

  return (
    <div className="module-completion">
      <div className="module-completion-summary" aria-live="polite">
        <span>
          {summary?.allModulesCompleted ?? 0} of {summary?.totalStudents ?? students.length}{' '}
          students finished all modules
        </span>
        <span className="module-completion-rate">{summary?.completionRate || '0%'}</span>
      </div>

      <div className="module-completion-scroll">
        <table className="module-completion-table">
          <thead>
            <tr>
              <th scope="col" className="module-completion-sticky">
                Student
              </th>
              {modules.map((mod) => (
                <th key={mod.id} scope="col">
                  <span className="module-completion-mod-num">M{mod.moduleNumber}</span>
                  <span className="module-completion-mod-name">{mod.moduleName}</span>
                </th>
              ))}
              <th scope="col">All modules</th>
            </tr>
          </thead>
          <tbody>
            {students.map((student) => (
              <tr
                key={student.enrollmentId}
                className={
                  student.allModulesComplete ? 'module-completion-row--done' : undefined
                }
              >
                <th scope="row" className="module-completion-sticky">
                  <div className="module-completion-student">
                    <span className="module-completion-student-name">
                      {student.fullName || student.studentId}
                    </span>
                    {student.emailAddress ? (
                      <span className="module-completion-student-meta">
                        {student.emailAddress}
                      </span>
                    ) : null}
                  </div>
                </th>
                {modules.map((mod) => {
                  const cell = studentProgressCell(student, mod.id);
                  const key = `${student.enrollmentId}:${mod.id}`;
                  const busy = savingKey === key;
                  const completed = cell.status === MODULE_STATUS.COMPLETED;
                  return (
                    <td key={mod.id}>
                      <select
                        className={`module-completion-select${
                          completed ? ' module-completion-select--done' : ''
                        }`}
                        value={completed ? MODULE_STATUS.COMPLETED : MODULE_STATUS.ONGOING}
                        disabled={busy}
                        aria-label={`${student.fullName || student.studentId}: ${mod.moduleName}`}
                        onChange={(e) =>
                          onStatusChange(student.enrollmentId, mod.id, e.target.value)
                        }
                      >
                        <option value={MODULE_STATUS.ONGOING}>Ongoing</option>
                        <option value={MODULE_STATUS.COMPLETED}>Completed</option>
                      </select>
                    </td>
                  );
                })}
                <td>
                  {student.allModulesComplete ? (
                    <span className="module-completion-badge module-completion-badge--done">
                      Complete
                    </span>
                  ) : (
                    <span className="module-completion-badge">In progress</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
